import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GraduationCap, Plus, Pencil, Trash2, KeyRound, Unlock, Power, Search } from "lucide-react";
import { toast } from "sonner";
import { apiCall, copyText } from "@/components/admin/people/peopleUtils";

export default function AdminStudents() {
  const [tab, setTab] = useState("b2c"); // b2c | school
  const [students, setStudents] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const loadSchools = useCallback(async () => {
    try { const d = await apiCall("/admin/schools?limit=100"); setSchools(d.schools || []); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "b2c") {
        const d = await apiCall("/admin/auth/subscribers/students");
        setStudents(d.students || []);
      } else {
        const q = schoolFilter !== "all" ? `?school_id=${schoolFilter}&limit=200` : "?limit=200";
        const d = await apiCall(`/admin/schools/all-students${q}`);
        setStudents(d.students || []);
      }
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [tab, schoolFilter]);

  useEffect(() => { loadSchools(); }, [loadSchools]);
  useEffect(() => { load(); }, [load]);

  const filtered = students.filter((s) => {
    const q = search.toLowerCase();
    return !q || (s.name || "").toLowerCase().includes(q) || (s.student_id || "").toLowerCase().includes(q) || (s.parent_email || "").toLowerCase().includes(q);
  });

  const isActive = (s) => (s.active !== undefined ? s.active : s.status !== "inactive");

  const openCreate = () => {
    setForm(tab === "b2c" ? { name: "", parent_email: "", grade: "", phone: "" } : { name: "", school_id: schoolFilter !== "all" ? schoolFilter : "", grade: "", section: "" });
    setModal({ mode: "create" });
  };
  const openEdit = (s) => {
    setForm(tab === "b2c" ? { name: s.name || "", grade: s.grade || "", phone: s.phone || "", parent_email: s.parent_email || "" } : { name: s.name || "", grade: s.grade || "", section: s.section || "", roll_number: s.roll_number || "" });
    setModal({ mode: "edit", data: s });
  };

  const save = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (tab === "b2c") {
        if (modal.mode === "create") {
          if (!form.parent_email) { toast.error("Parent email is required"); setSaving(false); return; }
          await apiCall("/admin/auth/subscribers/students", { method: "POST", body: form });
        } else {
          await apiCall(`/admin/auth/subscribers/students/${modal.data.id}`, { method: "PUT", body: form });
        }
      } else {
        if (modal.mode === "create") {
          if (!form.school_id) { toast.error("School is required"); setSaving(false); return; }
          await apiCall("/admin/people/school-students", { method: "POST", body: form });
        } else {
          await apiCall(`/admin/people/school-students/${modal.data.id}`, { method: "PUT", body: form });
        }
      }
      toast.success(modal.mode === "create" ? "Student created" : "Student updated");
      setModal(null); load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const act = async (s, action) => {
    try {
      const b2c = tab === "b2c";
      if (action === "toggle") {
        const next = !isActive(s);
        const path = b2c ? `/admin/auth/subscribers/students/${s.id}/toggle` : `/admin/people/school-students/${s.id}/toggle`;
        await apiCall(path, { method: "POST", body: { active: next } });
        toast.success(next ? "Activated" : "Deactivated");
      } else if (action === "delete") {
        if (!window.confirm(`Delete student ${s.name}? This cannot be undone.`)) return;
        const path = b2c ? `/admin/auth/subscribers/students/${s.id}` : `/admin/people/school-students/${s.id}`;
        await apiCall(path, { method: "DELETE" });
        toast.success("Student deleted");
      } else if (action === "reset" && b2c) {
        const d = await apiCall(`/admin/users/student/${s.id}/reset-password`, { method: "POST" });
        if (d.temp_password) copyText(d.temp_password, "Temp password"); else toast.success("Reset done");
      } else if (action === "unlock" && b2c) {
        await apiCall(`/admin/users/student/${s.id}/unlock`, { method: "POST" });
        toast.success("Unlocked");
      }
      load();
    } catch (e) { toast.error(e.message); }
  };

  const TabBtn = ({ id, label }) => (
    <button onClick={() => setTab(id)} data-testid={`students-tab-${id}`}
      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === id ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{label}</button>
  );

  return (
    <div className="space-y-6" data-testid="admin-students-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><GraduationCap className="h-6 w-6 text-emerald-600" /> Students</h1>
          <p className="text-sm text-slate-500">Manage B2C subscriber students and B2B school students</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="student-create-btn"><Plus className="h-4 w-4 mr-2" /> Add Student</Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <TabBtn id="b2c" label="B2C (Subscribers)" />
        <TabBtn id="school" label="School (B2B)" />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search students" className="pl-10" data-testid="students-search" />
        </div>
        {tab === "school" && (
          <select className="border border-slate-200 rounded-md p-2 text-sm" value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)} data-testid="students-school-filter">
            <option value="all">All schools</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left">
            <tr><th className="p-4">Name</th><th className="p-4">{tab === "b2c" ? "Student ID" : "School"}</th><th className="p-4">Grade</th><th className="p-4">{tab === "b2c" ? "Parent" : "Section"}</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={6} className="p-8 text-center text-slate-400">Loading…</td></tr>)
              : filtered.length === 0 ? (<tr><td colSpan={6} className="p-8 text-center text-slate-400">No students found</td></tr>)
              : filtered.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`student-row-${s.id}`}>
                  <td className="p-4 font-medium text-slate-800">{s.name || "—"}</td>
                  <td className="p-4 text-slate-600">{tab === "b2c" ? (s.student_id || "—") : (s.school_name || s.school_code || "—")}</td>
                  <td className="p-4 text-slate-600">{s.grade || "—"}</td>
                  <td className="p-4 text-slate-600">{tab === "b2c" ? (s.parent_email || "—") : (s.section || "—")}</td>
                  <td className="p-4"><Badge className={isActive(s) ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>{isActive(s) ? "active" : "inactive"}</Badge></td>
                  <td className="p-4">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(s)} data-testid={`student-edit-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" title="Activate/Deactivate" onClick={() => act(s, "toggle")} data-testid={`student-toggle-${s.id}`}><Power className="h-4 w-4" /></Button>
                      {tab === "b2c" && <Button size="sm" variant="ghost" title="Reset password" onClick={() => act(s, "reset")} data-testid={`student-reset-${s.id}`}><KeyRound className="h-4 w-4" /></Button>}
                      {tab === "b2c" && <Button size="sm" variant="ghost" title="Unlock" onClick={() => act(s, "unlock")} data-testid={`student-unlock-${s.id}`}><Unlock className="h-4 w-4" /></Button>}
                      <Button size="sm" variant="ghost" title="Delete" className="text-red-600 hover:bg-red-50" onClick={() => act(s, "delete")} data-testid={`student-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="student-modal">
          <DialogHeader><DialogTitle>{modal?.mode === "create" ? "Add Student" : "Edit Student"} · {tab === "b2c" ? "B2C" : "School"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Student name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="student-name-input" />
            {tab === "b2c" ? (
              <>
                <Input placeholder="Parent email" value={form.parent_email || ""} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} disabled={modal?.mode === "edit"} data-testid="student-parent-input" />
                <Input placeholder="Phone (optional)" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </>
            ) : (
              <>
                <select className="w-full border border-slate-200 rounded-md p-2 text-sm" value={form.school_id || ""} onChange={(e) => setForm({ ...form, school_id: e.target.value })} disabled={modal?.mode === "edit"} data-testid="student-school-select">
                  <option value="">Select school…</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                <Input placeholder="Section (optional)" value={form.section || ""} onChange={(e) => setForm({ ...form, section: e.target.value })} />
              </>
            )}
            <Input placeholder="Grade" value={form.grade || ""} onChange={(e) => setForm({ ...form, grade: e.target.value })} data-testid="student-grade-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="student-save-btn">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
